/** Client mirror of server food source constants (data health filtering). */

import { CHAIN_BRANCH_LOCATOR_SOURCE_KEYS } from "./foodChainBranchLocatorConstants.js";

export const FOOD_SOURCE_KEYS = Object.freeze({
  EXITO_SUPERMARKET: "EXITO_SUPERMARKET",
  CARULLA_SUPERMARKET: "CARULLA_SUPERMARKET",
  JUMBO_COLOMBIA: "JUMBO_COLOMBIA",
  OLIMPICA_COLOMBIA: "OLIMPICA_COLOMBIA",
  D1_COLOMBIA: "D1_COLOMBIA",
  ARA_COLOMBIA: "ARA_COLOMBIA",
  SUPERMU_COLOMBIA: "SUPERMU_COLOMBIA",
});

export const FOOD_SIPSA_SOURCE_KEYS = Object.freeze({
  WHOLESALE: "DANE_SIPSA_WHOLESALE",
  SUPPLY: "DANE_SIPSA_SUPPLY",
  INPUTS: "DANE_SIPSA_INPUTS",
});

export const FOOD_OSM_SOURCE_KEYS = Object.freeze({
  FOOD_POIS: "OSM_FOOD_POIS",
});

export const FOOD_INPUT_REFERENCE_SOURCE_KEYS = Object.freeze({
  FUEL: "FUEL_PRICE_REFERENCE",
  PACKAGING: "PACKAGING_PRICE_REFERENCE",
});

export const FOOD_BRANCH_LOCATOR_SOURCE_KEYS = CHAIN_BRANCH_LOCATOR_SOURCE_KEYS;

export const FOOD_SEEDED_SOURCE_KEYS = Object.freeze([
  ...Object.values(FOOD_SOURCE_KEYS),
  ...Object.values(FOOD_SIPSA_SOURCE_KEYS),
  ...Object.values(FOOD_INPUT_REFERENCE_SOURCE_KEYS),
  ...Object.values(FOOD_OSM_SOURCE_KEYS),
]);

export const FOOD_LAYER_IDS = Object.freeze({
  SUPERMARKETS: "foodSupermarkets",
  PRICE_HEATMAP: "foodPriceHeatmap",
  BASIC_BASKET_ZONES: "foodBasicBasketZones",
  ACCESS_GAPS: "foodAccessGaps",
});

export const FOOD_SOURCE_GROUP_LABELS = {
  retail_supermarket: "Retail supermarkets",
  sipsa_wholesale: "SIPSA wholesale",
  sipsa_supply: "SIPSA supply",
  sipsa_inputs: "SIPSA farm inputs",
  input_reference: "Input reference",
  osm_poi: "OSM food POIs",
  chain_branch_locator: "Chain branch locators",
  food_other: "Food",
  food_registry: "Food registry",
};

export function isFoodDataHealthSource(sourceCode) {
  const normalized = String(sourceCode || "").trim();
  if (!normalized) {
    return false;
  }
  if (FOOD_SEEDED_SOURCE_KEYS.includes(normalized)) {
    return true;
  }
  if (normalized.startsWith("DANE_SIPSA_")) {
    return true;
  }
  if (normalized.startsWith("OSM_FOOD")) {
    return true;
  }
  if (Object.values(FOOD_INPUT_REFERENCE_SOURCE_KEYS).includes(normalized)) {
    return true;
  }
  if (normalized.endsWith("_BRANCH_LOCATOR")) {
    return true;
  }
  return false;
}

export function isFoodRetailSupermarketDataHealthSource(source) {
  return source?.sourceGroup === "retail_supermarket";
}

export function foodSourceGroupLabel(sourceGroup) {
  return FOOD_SOURCE_GROUP_LABELS[sourceGroup] || null;
}

export function filterRetailSupermarketDataHealthSources(sources = []) {
  return sources.filter(isFoodRetailSupermarketDataHealthSource);
}

export function isFoodSipsaDataHealthSource(source) {
  const key = String(source?.sourceCode || source?.sourceKey || "").trim();
  return Object.values(FOOD_SIPSA_SOURCE_KEYS).includes(key);
}

export function filterSipsaDataHealthSources(sources = []) {
  return sources.filter(isFoodSipsaDataHealthSource);
}

export function isFoodInputReferenceDataHealthSource(source) {
  const key = String(source?.sourceCode || source?.sourceKey || "").trim();
  return Object.values(FOOD_INPUT_REFERENCE_SOURCE_KEYS).includes(key);
}

export function filterInputReferenceDataHealthSources(sources = []) {
  return sources.filter(isFoodInputReferenceDataHealthSource);
}

export function isFoodOsmPoiDataHealthSource(source) {
  const key = String(source?.sourceCode || source?.sourceKey || "").trim();
  return key === FOOD_OSM_SOURCE_KEYS.FOOD_POIS;
}

export function filterOsmPoiDataHealthSources(sources = []) {
  return sources.filter(isFoodOsmPoiDataHealthSource);
}

export function isFoodBranchLocatorDataHealthSource(source) {
  const key = String(source?.sourceCode || source?.sourceKey || "").trim();
  return Object.values(FOOD_BRANCH_LOCATOR_SOURCE_KEYS).includes(key);
}

export function filterBranchLocatorDataHealthSources(sources = []) {
  return sources.filter(isFoodBranchLocatorDataHealthSource);
}

export function foodSourceHasAutomatedRunner(source) {
  return (
    isFoodRetailSupermarketDataHealthSource(source) ||
    isFoodSipsaDataHealthSource(source) ||
    isFoodOsmPoiDataHealthSource(source) ||
    isFoodInputReferenceDataHealthSource(source) ||
    isFoodBranchLocatorDataHealthSource(source)
  );
}

export const FOOD_RETAIL_PLAYBOOK_SOURCES = Object.freeze([
  FOOD_SOURCE_KEYS.ARA_COLOMBIA,
  FOOD_SOURCE_KEYS.EXITO_SUPERMARKET,
  FOOD_SOURCE_KEYS.CARULLA_SUPERMARKET,
  FOOD_SOURCE_KEYS.JUMBO_COLOMBIA,
  FOOD_SOURCE_KEYS.D1_COLOMBIA,
  FOOD_SOURCE_KEYS.OLIMPICA_COLOMBIA,
  FOOD_SOURCE_KEYS.SUPERMU_COLOMBIA,
]);

export const FOOD_RETAIL_SOURCE_LABELS = {
  [FOOD_SOURCE_KEYS.ARA_COLOMBIA]: "Ara Colombia",
  [FOOD_SOURCE_KEYS.EXITO_SUPERMARKET]: "Éxito",
  [FOOD_SOURCE_KEYS.CARULLA_SUPERMARKET]: "Carulla",
  [FOOD_SOURCE_KEYS.JUMBO_COLOMBIA]: "Jumbo",
  [FOOD_SOURCE_KEYS.D1_COLOMBIA]: "D1",
  [FOOD_SOURCE_KEYS.OLIMPICA_COLOMBIA]: "Olímpica",
  [FOOD_SOURCE_KEYS.SUPERMU_COLOMBIA]: "SuperMu",
};

export function foodRetailSourceLabel(sourceKey) {
  return FOOD_RETAIL_SOURCE_LABELS[sourceKey] || sourceKey || "Retail source";
}

export function isFoodRetailPlaybookSource(sourceKey) {
  return FOOD_RETAIL_PLAYBOOK_SOURCES.includes(String(sourceKey || "").trim());
}

export function foodSourceDetailLines(source) {
  const metadata = source?.foodMetadata;
  if (!metadata) {
    return [];
  }

  const lines = [];
  const collection = metadata.collection;

  if (collection) {
    if (collection.retailObservationCount > 0) {
      lines.push(`Retail prices: ${collection.retailObservationCount.toLocaleString()}`);
    }
    if (collection.wholesaleObservationCount > 0) {
      lines.push(`Wholesale prices: ${collection.wholesaleObservationCount.toLocaleString()}`);
    }
    if (collection.supplyObservationCount > 0) {
      lines.push(`Supply observations: ${collection.supplyObservationCount.toLocaleString()}`);
    }
    if (collection.inputObservationCount > 0) {
      lines.push(`Input prices: ${collection.inputObservationCount.toLocaleString()}`);
    }
    if (collection.basketSnapshotCount > 0) {
      lines.push(`Basket snapshots: ${collection.basketSnapshotCount.toLocaleString()}`);
    }
    if (collection.storeCount > 0) {
      lines.push(`Mapped stores: ${collection.storeCount.toLocaleString()}`);
    }
    if (collection.latestActivityAt) {
      lines.push(`Latest data: ${new Date(collection.latestActivityAt).toLocaleString()}`);
    }
    if (collection.totalRecordsReceived === 0 && collection.basketSnapshotCount === 0) {
      lines.push("No observations collected yet");
    }
  }

  if (metadata.accessMethod) {
    lines.push(`Access: ${metadata.accessMethod}`);
  }

  if (source.isCollecting === false && collection?.totalRecordsReceived === 0) {
    if (source.isActive) {
      lines.push("Enabled — run ingestion to collect data");
    } else {
      lines.push("Not collecting yet");
    }
  } else if (source.isCollecting && source.isActive) {
    lines.push("Collecting data");
  } else if (source.isCollecting && !source.isActive) {
    lines.push("Manual PoC data on file — enable source to resume collection");
  }

  return lines;
}
